"use client";

import { useMemo } from "react";
import { CARD, TABLE, TD, TH, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  BarChart,
  DensityChart,
} from "@/components/tools/stats-distributions/charts";
import { SamplingHistogram } from "@/components/tools/stats-distributions-more/clt-charts";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  barGeometry,
  densityGeometry,
} from "@/lib/tool-kit/stats-distributions/chart";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";
import {
  BINARY_P,
  histogramOf,
  type Moments,
  type Population,
  shareWithin,
  theoryForMean,
} from "@/lib/tool-kit/stats-distributions-more/clt";
import { histogramGeometry } from "@/lib/tool-kit/stats-distributions-more/clt-chart";

/** The strings these parts use; the widget's full set is a superset of this. */
export interface CltPartLabels {
  populationHeading: string;
  populationLine: string;
  populationChartLabel: string;
  binaryChartLabel: string;
  meansHeading: string;
  meansChartLabel: string;
  meansCaption: string;
  outsideNote: string;
  tableCaption: string;
  tableMeasure: string;
  tableTheory: string;
  tableSimulated: string;
  rowMean: string;
  rowSe: string;
  rowSkew: string;
  rowWithin: string;
  fitClose: string;
  fitSkewed: string;
}

const BINS = 32;
const SPAN_SES = 4;

export function PopulationSketch({
  population,
  labels,
  fmt,
}: {
  population: Population;
  labels: CltPartLabels;
  fmt: NumberFormatter;
}) {
  const sig = (value: number) => fmt(value, { maximumSignificantDigits: 4 });
  const density = useMemo(() => {
    const { pdf, range } = population;
    if (!pdf) {
      return null;
    }
    return densityGeometry({
      pdf,
      xMin: range[0],
      xMax: range[1],
      shaded: [],
      markers: [population.mean],
      samples: population.id === "uniform" ? 480 : 240,
    });
  }, [population]);
  const bars = useMemo(
    () =>
      barGeometry([
        { k: 0, p: 1 - BINARY_P },
        { k: 1, p: BINARY_P },
      ]),
    []
  );
  return (
    <div className={`${CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{labels.populationHeading}</h3>
      {density ? (
        <DensityChart
          format={(value) => sig(value)}
          geometry={density}
          label={labels.populationChartLabel}
        />
      ) : (
        <BarChart
          format={(value) => fmt(value)}
          geometry={bars}
          highlighted={() => true}
          label={labels.binaryChartLabel}
        />
      )}
      <p className="text-muted-foreground text-sm">
        {t(labels.populationLine, {
          mean: sig(population.mean),
          sd: sig(population.sd),
          skew: sig(population.skewness),
        })}
      </p>
    </div>
  );
}

export function MeansPanel({
  population,
  means,
  shown,
  total,
  n,
  labels,
  locale,
  fmt,
}: {
  population: Population;
  means: Float64Array;
  /** How many of the means to draw: all of them, or the first few during an animation. */
  shown: number;
  total: number;
  n: number;
  labels: CltPartLabels;
  locale: string;
  fmt: NumberFormatter;
}) {
  const theory = theoryForMean(population, n);
  const lo = theory.mean - SPAN_SES * theory.se;
  const hi = theory.mean + SPAN_SES * theory.se;
  const histogram = histogramOf(means, shown, lo, hi, BINS);
  const fullPeak = useMemo(
    () => Math.max(...histogramOf(means, total, lo, hi, BINS).counts),
    [means, total, lo, hi]
  );
  const geometry = histogramGeometry(
    histogram.counts,
    lo,
    hi,
    { mean: theory.mean, sd: theory.se },
    { scaleTotal: total, shownTotal: shown, peakCount: fullPeak }
  );
  const sig = (value: number) => fmt(value, { maximumSignificantDigits: 3 });
  return (
    <div className={`${CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{labels.meansHeading}</h3>
      <SamplingHistogram
        format={sig}
        geometry={geometry}
        label={labels.meansChartLabel}
      />
      <p className="text-muted-foreground text-sm">
        {t(labels.meansCaption, {
          shown: fmt(shown),
          n: fmt(n),
          mean: fmt(theory.mean, { maximumSignificantDigits: 4 }),
          se: fmt(theory.se, { maximumSignificantDigits: 4 }),
        })}
      </p>
      {histogram.outside > 0 ? (
        <p className="text-muted-foreground text-sm">
          {tPlural(labels.outsideNote, locale, histogram.outside, {
            count: fmt(histogram.outside),
          })}
        </p>
      ) : null}
    </div>
  );
}

export function ComparisonTable({
  population,
  means,
  moments,
  n,
  labels,
  fmt,
}: {
  population: Population;
  means: Float64Array;
  moments: Moments;
  n: number;
  labels: CltPartLabels;
  fmt: NumberFormatter;
}) {
  const theory = theoryForMean(population, n);
  const four = (value: number) =>
    fmt(value, { maximumSignificantDigits: 4, minimumFractionDigits: 0 });
  const within = shareWithin(
    means,
    moments.count,
    theory.mean,
    theory.se,
    1.96
  );
  const percent = (value: number) =>
    fmt(value, {
      style: "percent",
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  const rows: [string, string, string][] = [
    [labels.rowMean, four(theory.mean), four(moments.mean)],
    [labels.rowSe, four(theory.se), four(moments.sd)],
    [labels.rowSkew, four(theory.skewness), four(moments.skewness)],
    [labels.rowWithin, percent(0.95), percent(within)],
  ];
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <caption className="px-3 pt-3 text-left text-muted-foreground text-sm">
          {labels.tableCaption}
        </caption>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {labels.tableMeasure}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {labels.tableTheory}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {labels.tableSimulated}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([measure, expected, observed]) => (
            <tr key={measure}>
              <th className={`${TD} font-medium`} scope="row">
                {measure}
              </th>
              <td className={`${TD} text-right font-mono`}>{expected}</td>
              <td className={`${TD} text-right font-mono`}>{observed}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** How well the normal curve should fit at this n, from the predicted skewness. */
export function fitMessage(
  population: Population,
  n: number,
  labels: CltPartLabels,
  fmt: NumberFormatter
): string {
  const skew = theoryForMean(population, n).skewness;
  const text = fmt(skew, { maximumSignificantDigits: 3 });
  return t(Math.abs(skew) < 0.25 ? labels.fitClose : labels.fitSkewed, {
    skew: text,
    n: fmt(n),
  });
}
